import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import './App.css';
import ZodiacNav, { ZODIAC_SIGNS } from './components/ZodiacNav';
import HoroscopeDisplay from './components/HoroscopeDisplay';
import AdBanner from './components/AdBanner';
import AdSidebar from './components/AdSidebar';
import AdRectangle from './components/AdRectangle';
import LanguageSelector from './components/LanguageSelector';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Cookies from './pages/Cookies';
import ZodiacSignsRelationships from './pages/ZodiacSignsRelationships';
import MoonPhasesPersonality from './pages/MoonPhasesPersonality';
import CompleteGuideAries from './pages/CompleteGuideAries';
import AstrologyVsAstronomy from './pages/AstrologyVsAstronomy';
import HowBirthChartsWork from './pages/HowBirthChartsWork';
import AriesPersonality from './pages/AriesPersonality';
import MoonPhasesMood from './pages/MoonPhasesMood';
import ZodiacCompatibility from './pages/ZodiacCompatibility';
import MercuryRetrograde from './pages/MercuryRetrograde';
import WesternVsVedicAstrology from './pages/WesternVsVedicAstrology';

// Use env if set; otherwise localhost in dev, production URL in production
const API_BASE_URL =
  process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL.replace(/\/$/, '') // strip trailing slash
    : process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000'
      : 'https://dailyzodiac.ai';

function HomePage() {
  const { i18n, t } = useTranslation();
  const [horoscopes, setHoroscopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentLang = (i18n.language || 'en').split(/[-_]/)[0].toLowerCase() || 'en';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchAll = async () => {
      try {
        let response = await fetch(`${API_BASE_URL}/horoscopes?lang=${currentLang}`);
        if (!response.ok && currentLang !== 'en') {
          response = await fetch(`${API_BASE_URL}/horoscopes?lang=en`);
        }
        if (!response.ok) {
          await response.json().catch(() => ({}));
          throw new Error(t('notGenerated'));
        }
        const data = await response.json();
        if (!cancelled) setHoroscopes(data.horoscopes || []);
      } catch (err) {
        if (!cancelled) {
          const msg = err.message || t('noHoroscope');
          const isNetworkError = msg === 'Failed to fetch' || msg.includes('NetworkError');
          setError(isNetworkError
            ? `${msg} — Check that the API is reachable (same domain as this site) and try again.`
            : msg);
          setHoroscopes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [currentLang, t]);

  const bySign = horoscopes.reduce((acc, h) => {
    acc[h.zodiac_sign.toLowerCase()] = h;
    return acc;
  }, {});

  const hasAnyHoroscopes = horoscopes.length > 0;

  return (
    <>
      <ZodiacNav />
      <div className="main-container main-with-nav">
        <main className="content">
          <header className="app-header">
            <LanguageSelector />
            <h1>✨ <Trans i18nKey="title" /> ✨</h1>
            <p><Trans i18nKey="subtitle" /></p>
          </header>

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('loading')}</p>
            </div>
          )}

          {error && !loading && (
            <div className="error">
              <p>⚠️ {error}</p>
            </div>
          )}

          {!loading && (
            <div className="horoscopes-all">
              {ZODIAC_SIGNS.map((sign) => {
                const horoscope = bySign[sign.id];
                return (
                  <section
                    key={sign.id}
                    id={sign.id}
                    className="horoscope-section-anchor"
                    aria-labelledby={`heading-${sign.id}`}
                  >
                    {horoscope ? (
                      <>
                        <HoroscopeDisplay horoscope={horoscope} />
                        <div className="ad-below-horoscope">
                          <AdRectangle size="medium" />
                        </div>
                      </>
                    ) : (
                      <div className="horoscope-placeholder">
                        <h2 id={`heading-${sign.id}`} className="horoscope-placeholder-heading">
                          {sign.symbol} {t(`zodiac.${sign.id}`)}
                        </h2>
                        <p className="horoscope-placeholder-text">{t('notGenerated')}</p>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}

          <section className="home-intro" aria-label="About this site">
            <h2><Trans i18nKey="homeIntro.heading" /></h2>
            <p><Trans i18nKey="homeIntro.paragraph1" /></p>
            <p><Trans i18nKey="homeIntro.paragraph2" /></p>
            <p><Trans i18nKey="homeIntro.paragraph3" /></p>
            <p className="home-intro-how">
              <strong><Trans i18nKey="homeIntro.howWeCreateHeading" />.</strong>{' '}
              <Trans i18nKey="homeIntro.howWeCreateText" />{' '}
              <Link to="/about"><Trans i18nKey="homeIntro.howWeCreateLink" /></Link>.
            </p>
          </section>
        </main>

        {hasAnyHoroscopes && !loading && !error && (
          <aside className="ad-sidebar right" aria-label="Advertisement">
            <AdSidebar />
          </aside>
        )}
      </div>

      {hasAnyHoroscopes && !loading && !error && (
        <div className="ad-container bottom" aria-label="Advertisement">
          <AdBanner />
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/articles/zodiac-signs-relationships" element={<ZodiacSignsRelationships />} />
          <Route path="/articles/moon-phases-personality" element={<MoonPhasesPersonality />} />
          <Route path="/articles/complete-guide-aries" element={<CompleteGuideAries />} />
          <Route path="/articles/astrology-vs-astronomy" element={<AstrologyVsAstronomy />} />
          <Route path="/articles/how-birth-charts-work" element={<HowBirthChartsWork />} />
          <Route path="/articles/aries-personality" element={<AriesPersonality />} />
          <Route path="/articles/moon-phases-mood" element={<MoonPhasesMood />} />
          <Route path="/articles/zodiac-compatibility" element={<ZodiacCompatibility />} />
          <Route path="/articles/mercury-retrograde" element={<MercuryRetrograde />} />
          <Route path="/articles/western-vs-vedic-astrology" element={<WesternVsVedicAstrology />} />
        </Routes>
        <Footer />
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
